import { createContext, useContext, useId, useState, ReactNode } from "react";
import { IconButton, Tooltip } from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";

import "./style.scss";

/**
 * Settings-dial standard (DESIGN_GUIDE.md §6): every tunable setting ("dial")
 * shows its default in the label, offers a per-dial reset that appears only
 * when the value differs from default, and explains itself in the shared help
 * strip while focused.
 *
 * DIALS is the single source of truth for label / default / explanation.
 * Defaults duplicate values that live in backend constants and flow nodes —
 * each entry says where; keep them in sync when a platform default changes.
 */
export interface DialMeta {
    label: string;
    // Rendered as "(default …)" in the label; null = the dial has no default
    // (e.g. embedding model) — label renders bare and reset is not offered.
    defaultText: string | null;
    help: string;
}

export const DIALS: Record<string, DialMeta> = {
    // --- Retrieval (knowledge_base_configs; defaults: prisma 0031 + kb.search) ---
    searchWeights: {
        label: "Search weights",
        defaultText: "balanced 0.5 / 0.5",
        help: "Balance between keyword (BM25) and semantic (vector) matching when candidate passages are ranked. Keyword favors exact terms; semantic favors meaning."
    },
    searchTopK: {
        label: "Search top K",
        defaultText: "50",
        help: "Candidate pool size per retrieval method before rank fusion. Larger pools surface deeper matches at more ranking cost."
    },
    searchResultLimit: {
        label: "Result limit",
        defaultText: "10",
        help: "Cap on passages returned to the answering model per search. On agentic chat this caps the agent's per-search topK."
    },
    rrfConstant: {
        label: "RRF constant",
        defaultText: "60",
        help: "Reciprocal-rank-fusion constant. Lower values weight top-ranked candidates more heavily when keyword and semantic rankings merge."
    },
    useReranker: {
        label: "Use reranker",
        defaultText: "on",
        help: "Re-orders the fused candidates with an LLM reranking pass before the top passages are chosen. Off uses the fused order directly."
    },
    rerankerModel: {
        label: "Reranker model",
        defaultText: "platform cascade",
        help: "Model for the reranking pass. The default cascade tries gpt-6-luna, then gpt-5.6-luna, claude-haiku-4-5, and gpt-5.4-mini, failing over on errors. Selecting a model makes it the cascade primary, with those fallbacks still behind it."
    },
    bm25Language: {
        label: "BM25 language",
        defaultText: "English",
        help: "Stemming and stop-word language for keyword search. Should match the corpus language for good keyword recall."
    },
    // --- Processing (knowledge_base_configs; defaults: prisma 0031 + flow nodes) ---
    recognitionMode: {
        label: "Recognition mode",
        defaultText: "rich document",
        help: "How PDFs are recognized: Rich Document (V2) reads the whole document with structure; Single Page (V1) is the faster, lower-cost per-page method."
    },
    pdfRecognitionModel: {
        label: "PDF recognition model",
        defaultText: "gemini-3.1-flash-lite",
        help: "Gemini model used to recognize PDF pages."
    },
    videoTranscriptionModel: {
        label: "Video transcription model",
        defaultText: "gemini-3-flash-preview",
        help: "Gemini model used to transcribe video and audio documents, one call per chunk. Applies to documents digested after the change."
    },
    thinkingLevel: {
        label: "Thinking level",
        defaultText: "Model default",
        help: "How much the Gemini model reasons before answering during PDF recognition and video transcription. Minimal is fastest and cheapest; High helps hard layouts at extra cost and time. Levels not supported by the chosen model fall back to the nearest one."
    },
    chunkSize: {
        label: "Chunk size",
        defaultText: "2500",
        help: "Target characters per chunk when documents are split for embedding. Larger chunks carry more context but fewer fit in a search result."
    },
    chunkOverlap: {
        label: "Chunk overlap",
        defaultText: "200",
        help: "Characters repeated between forced-split fragments of one block, so no sentence loses its surrounding context."
    },
    metadataExtractionModel: {
        label: "Extraction model",
        defaultText: "gemini-3.5-flash-lite",
        help: "Model for the digestion-policy metadata pass — one call per document at recognition time and on re-extract."
    },
    metadataSamplePages: {
        label: "Sample pages",
        defaultText: "3",
        help: "Pages of each document the metadata pass reads. Raise when policy fields live past the opening pages (e.g. schedules deep in a syllabus)."
    },
    redigestBatchSize: {
        label: "Re-digest batch size",
        defaultText: "10",
        help: "Legacy documents re-recognized per Re-digest run. Each document costs recognition per page."
    },
    videoChunkSeconds: {
        label: "Video chunk (seconds)",
        defaultText: "300",
        help: "Seconds of video transcribed per chunk."
    },
    embeddingModel: {
        label: "Embedding model",
        defaultText: null,
        help: "Vector model for chunk embeddings. Changing it deletes every stored chunk and requires a full re-embedding of all documents."
    },
    // --- Agent dials (agents.budgets / response_options; defaults:
    //     agentProvisioning DEFAULT_AGENT_BUDGETS + agent.run flow) ---
    maxIterations: {
        label: "Max iterations",
        defaultText: "8",
        help: "Model-call loop iterations allowed per turn before the agent finalizes best-effort."
    },
    maxTokens: {
        label: "Max tokens / turn",
        defaultText: "60000",
        help: "Token budget per turn — input, output, and reasoning, including consulted specialists' spend. Exhaustion forces a best-effort answer."
    },
    llmCallTimeoutMs: {
        label: "LLM call timeout (ms)",
        defaultText: "90000",
        help: "Timeout for a single model call."
    },
    toolTimeoutMs: {
        label: "Tool timeout (ms)",
        defaultText: "30000",
        help: "Timeout for a single tool call (consults keep a 120s floor)."
    },
    turnDeadlineMs: {
        label: "Turn deadline (ms)",
        defaultText: "300000",
        help: "Whole-turn deadline; at the deadline the loop finalizes best-effort with what it has."
    },
    searchTopKDefault: {
        label: "Search top K default",
        defaultText: "5",
        help: "Passages the agent asks for per search when it doesn't choose a size itself (the tool allows 1–20)."
    },
    historyWindow: {
        label: "History window (turns)",
        defaultText: "10",
        help: "Completed exchanges from this room included as conversation context on each turn."
    },
    askMaxPerConversation: {
        label: "Max questions per conversation",
        defaultText: "unlimited",
        help: "Cap on clarifying questions per conversation. At the cap the ask_user tool is no longer offered and the agent answers with what it has."
    },
    verbosity: {
        label: "Answer verbosity",
        defaultText: "standard",
        help: "Appends an answer-length ground rule to the agent's prompt. Standard adds nothing — today's behavior."
    },
    language: {
        label: "Answer language",
        defaultText: "the user's language",
        help: "Pin every answer to one language (ISO tag, e.g. en, pt-BR). Blank answers in the language of the user's question."
    }
};

/** "Sample pages (default 3)" — also used for MUI TextField label props. */
export const dialLabel = (dial: string, defaultOverride?: string): string => {
    const meta = DIALS[dial];
    if (!meta) return dial;
    const def = defaultOverride ?? meta.defaultText;
    return def === null ? meta.label : `${meta.label} (default ${def})`;
};

interface DialHelpContextValue {
    help: string | null;
    setHelp: (help: string | null) => void;
    helpId: string;
}

const DialHelpContext = createContext<DialHelpContextValue | null>(null);

/**
 * Wrap a settings form; hosts the focused-dial help state. Pass `helpId`
 * (from useId in the host) when inputs outside this file need to reference
 * the strip via aria-describedby.
 */
export function DialHelpProvider({
    helpId: helpIdProp,
    children
}: {
    helpId?: string;
    children: ReactNode;
}) {
    const [help, setHelp] = useState<string | null>(null);
    const generatedId = useId();
    const helpId = helpIdProp ?? generatedId;
    return (
        <DialHelpContext.Provider value={{ help, setHelp, helpId }}>
            {children}
        </DialHelpContext.Provider>
    );
}

/**
 * The fixed explanation strip (property-inspector pattern): renders the
 * focused dial's help text; keeps the last text on blur so it never flickers
 * while being read. role="status" announces the change to screen readers.
 */
export function DialHelpStrip() {
    const ctx = useContext(DialHelpContext);
    if (!ctx) return null;
    return (
        <p id={ctx.helpId} className="dial-help-strip" role="status">
            {ctx.help ?? "Focus a setting to see what it does."}
        </p>
    );
}

/** aria-describedby target for inputs inside a DialHelpProvider. */
export function useDialHelpId(): string | undefined {
    return useContext(DialHelpContext)?.helpId;
}

/**
 * One dial row: label with "(default …)", the control, and a reset button
 * that appears only when the value differs from default. Focus anywhere
 * inside puts the dial's explanation in the help strip.
 *
 * `noLabel` hosts controls that carry their own label (MUI TextField/Select) —
 * pass `dialLabel(dial)` as that control's label. `defaultOverride` handles
 * role-dependent defaults (e.g. orchestrator budgets).
 */
export function DialField({
    dial,
    htmlFor,
    changed,
    onReset,
    defaultOverride,
    noLabel = false,
    inline = true,
    children
}: {
    dial: string;
    htmlFor?: string;
    changed: boolean;
    onReset?: () => void;
    defaultOverride?: string;
    noLabel?: boolean;
    inline?: boolean;
    children: ReactNode;
}) {
    const ctx = useContext(DialHelpContext);
    const meta = DIALS[dial];
    const def = defaultOverride ?? meta?.defaultText ?? null;
    const showReset = changed && !!onReset && def !== null;
    return (
        <div
            className={`dial-field${inline ? " dial-field--inline" : ""}${noLabel ? " dial-field--nolabel" : ""}`}
            onFocusCapture={() => meta && ctx?.setHelp(meta.help)}
        >
            {!noLabel && (
                <label className="dial-field__label" htmlFor={htmlFor}>
                    {dialLabel(dial, defaultOverride)}
                </label>
            )}
            <div className="dial-field__control">{children}</div>
            <span className="dial-field__reset-slot">
                {showReset && (
                    <Tooltip title={`Reset to default (${def})`}>
                        <IconButton
                            className="dial-field__reset"
                            size="small"
                            aria-label={`Reset ${meta?.label ?? dial} to default (${def})`}
                            onClick={onReset}
                        >
                            <ReplayIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </span>
        </div>
    );
}
